/* eslint-disable react-hooks/exhaustive-deps */
import { IPageResponse, SortDirection } from "common/interfaces/IPageResponse";
import { IElectricityInvoice, IElectricityInvoiceRequest, IElectricityInvoiceSortFields } from "models/electricity/electricity-invoice";
import React, { Dispatch, useContext, useEffect, useState } from "react";
import { AuthenticationContext } from "services/context/authentication.context";
import {dateFormatOptions} from "../../../common/dateFormatterConfig";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import Pagination from "rc-pagination";
import {locale} from "../../../common/table/paginatorConfig";
import {SortIcons} from "../../SortIcons/SortIcons";
import { IFilePreview } from "common/interfaces/IFilePreview";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import ElectricityInvoiceService from "services/electricity/electricity-invoice.service";
import PDFPreviewModal from "components/Modals/PDFPreviewModal/PDFPreviewModal";
import printJS from "print-js";

interface ElectricityInvoicesTableProps {
  isRequesting: boolean;
  invoices: IPageResponse<IElectricityInvoice> | null;
  pageRequest: IElectricityInvoiceRequest;
  onPageChange: (targetPage: number) => void;
  onSortChange: (sortedByField: IElectricityInvoiceSortFields, sortDirection: SortDirection) => void;
}

export function ElectricityInvoicesTable(props: ElectricityInvoicesTableProps){
  const [sortedByField, setSortedByField]: [IElectricityInvoiceSortFields, Dispatch<any>] = useState(IElectricityInvoiceSortFields.ID);
  const [sortDirection, setSortDirection]: [SortDirection, Dispatch<any>] = useState(SortDirection.DESC);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);
  const [filePreview, setFilePreview] = useState<IFilePreview | null>(null);
  const [filePreviewLoading, setFilePreviewLoading] = useState<number>();
  const context = useContext(AuthenticationContext);

  const getInvoiceFile = async () => {
    if (context.state.customerId === null || isDownloading || !filePreview)
      return;
    setIsDownloading(true);

    try{
      await ElectricityInvoiceService.getInvoiceFileWithDownload(filePreview.id, filePreview.fileName);
    }finally{
      setIsDownloading(false)
    }      
  }

  const getInvoicePreview = async (invoice: IElectricityInvoice) => {
    if (context.state.customerId === null || filePreviewLoading)
      return;

      setFilePreviewLoading(invoice.id);

    try{
      const res = await ElectricityInvoiceService.getInvoiceFile(
        invoice.id
      );   

      setFilePreview({
        id: invoice.id,
        fileName: invoice.invoiceNumber.toString(),
        objectUrl: res.data,
      });

    }finally{
      setFilePreviewLoading(undefined)
    }
  }

  useEffect(() => {
    props.onSortChange(sortedByField, sortDirection);
  }, [sortedByField, sortDirection]);

  const invoices: Array<IElectricityInvoice> | undefined = props.invoices?.content;
  const pageResponse = props.invoices;

  const invoicesTableRows = invoices?.map((invoice: IElectricityInvoice) =>
    <tr key={'row-' + invoice.id}>
      <td>
        <div className="font-weight-bold">
          <span className="d-md-inline d-none">{invoice.invoiceNumber}</span>
        </div>
      </td>
      <td>
        {new Intl.DateTimeFormat('et', dateFormatOptions).format(new Date(invoice.dateIssued))}
      </td>
      <td>
        {new Intl.DateTimeFormat('et', dateFormatOptions).format(new Date(invoice.dateDue))}
      </td>
      <td>
        {invoice.amountIncludingVat}
      </td>
      <td>
        {invoice.amountExcludingVat}
      </td>
      <ProsumptionCell 
        primary={invoice.totalConsumption}
        secondary={invoice.totalProduction}
      />
      <ProsumptionCell 
        primary={invoice.consumptionAveragePrice}
        secondary={invoice.productionAveragePrice}
      />
      <td>
          <div className="row__actions">
              <OverlayTrigger 
                    delay={250}
                    overlay={
                      <Tooltip id={`tooltip-electricity-invoice-${invoice.id}`}>
                          <p style={{margin: '3px'}}>Vaata</p>
                      </Tooltip>
                    } 
                    placement={"auto"}
                  >
                    <Button 
                      size="sm" 
                      variant="secondary" 
                      className="d-flex align-items-center btn-outline-icon" 
                      onClick={() => getInvoicePreview(invoice)}
                      >
                          {
                            filePreviewLoading === invoice.id ? (
                              <Spinner size="sm" animation="border"/>
                            ) : (
                              <div className='icon__preview-file action-icon'></div>
                            )
                          }
                    </Button>
                  </OverlayTrigger>
          </div>
      </td>
    </tr>
  );

  const handleSortClick = (currentSortColumn: IElectricityInvoiceSortFields) => {
    if (sortedByField.match(currentSortColumn)) {
      setSortDirection(sortDirection === SortDirection.DESC ? SortDirection.ASC : SortDirection.DESC);
    } else {
      setSortDirection(SortDirection.ASC);
      setSortedByField(currentSortColumn);
    }
  };

  return (
    <>
      <div className="table__container scroll-icon-md">
        <table className='invoices__table table table-responsive-md table-hover'>
          <thead>
          <tr>
            <th className="sort-down-up-hover">
              <div className="d-flex align-items-center"
                  onClick={() => handleSortClick(IElectricityInvoiceSortFields.INVOICE_NUMBER)}>
                <span>Arve</span>
                <SortIcons
                  sortedByField={sortedByField}
                  sortDirection={sortDirection}
                  currentSortColumn={IElectricityInvoiceSortFields.INVOICE_NUMBER}/>
              </div>
            </th>
            <th className="sort-down-up-hover">
              <div className="d-flex align-items-center" onClick={() => handleSortClick(IElectricityInvoiceSortFields.DATE_ISSUED)}>
                <span>Kuupäev</span>
                <SortIcons
                  sortedByField={sortedByField}
                  sortDirection={sortDirection}
                  currentSortColumn={IElectricityInvoiceSortFields.DATE_ISSUED}/>
              </div>
            </th>
            <th className="sort-down-up-hover">
              <div className="d-flex align-items-center" onClick={() => handleSortClick(IElectricityInvoiceSortFields.DATE_DUE)}>
                <span>Maksetähtaeg</span>
                <SortIcons
                  sortedByField={sortedByField}
                  sortDirection={sortDirection}
                  currentSortColumn={IElectricityInvoiceSortFields.DATE_DUE}/>
              </div>
            </th>
            <th className="sort-down-up-hover">
              <div className="d-flex align-items-center" onClick={() => handleSortClick(IElectricityInvoiceSortFields.AMOUNT_INCLUDING_VAT)}>
                <span>Summa</span>
                <SortIcons
                  sortedByField={sortedByField}
                  sortDirection={sortDirection}
                  currentSortColumn={IElectricityInvoiceSortFields.AMOUNT_INCLUDING_VAT}/>
              </div>
            </th>
            <th className="sort-down-up-hover">
              <div className="d-flex align-items-center" onClick={() => handleSortClick(IElectricityInvoiceSortFields.AMOUNT_EXCLUDING_VAT)}>
                <span>Summa KM-ta</span>
                <SortIcons
                  sortedByField={sortedByField}
                  sortDirection={sortDirection}
                  currentSortColumn={IElectricityInvoiceSortFields.AMOUNT_EXCLUDING_VAT}/>
              </div>
            </th>
            <th>Kogus</th>
            <th>Keskmine ühikhind</th>
            <th/>
          </tr>
          </thead>
          <tbody>
            {props.isRequesting ?
              <tr>
                <td colSpan={8} className="spinner-cell">
                  <div className="p-4 w-100 max-w-100vw d-flex align-items-center justify-content-center">
                    <Spinner animation="grow" />
                  </div>
                </td>
              </tr> :
              invoicesTableRows
            }
          </tbody>
          <tfoot>
          {pageResponse ? <tr>
            <td colSpan={8}>
              <div className="pagination-wrapper">
                <Pagination
                  locale={locale}
                  current={pageResponse.number + 1}
                  total={pageResponse.totalElements}
                  pageSize={pageResponse.size}
                  prefixCls={"pagination"}
                  onChange={props.onPageChange}
                  showSizeChanger={false}
                  hideOnSinglePage={true}
                />
              </div>
            </td>
          </tr> : null}
          </tfoot>
        </table>
        </div>
        <PDFPreviewModal
          show={!!filePreview}
          onHide={() => setFilePreview(null)}
          onDownload={() => getInvoiceFile()}
          isDownloading={isDownloading}
          onPrint={() => printJS({printable: filePreview?.objectUrl, type: 'pdf'})}
          pdfPreview={filePreview?.objectUrl}
        />
    </>
  )
}

function ProsumptionCell({
  primary, 
  secondary
}: {primary: number, secondary: number}) {

  if(!primary){
    return (
      <td>
        -
      </td>
    )
  }

  return (
    <td>
      <div className="line-height-1">
        { !!secondary && 'Tarbimine:'} { primary }
      </div>
      { !!secondary && (
        <div className="line-height-1 pr-1">
          Tootmine: { secondary }
        </div>
        )
      }
    </td>
  )
}